import { Code2, HelpCircle, Maximize2, Minimize2, RefreshCw } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { ApiRequestError } from '@/shared/api/api-error';
import { cn } from '@/shared/lib/cn';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog';
import { UsageBalance } from '@/shared/usage/usage-balance';
import { useEditorFullscreen } from '@/shared/workspace/use-editor-fullscreen';

import { backupAge } from '../backup-time';
import {
  workspaceProgress,
  type WorkspaceAction,
  type WorkspaceAvailability,
} from '../workspace-progress';

import { WorkshopPreview } from './workshop-preview';
import { WorkshopProgress } from './workshop-progress';

import type { useIdeLaunch } from '../api/use-workshop-session';
import type { CurrentWorkspace, WorkspaceOperation } from '@mio/shared';

/** The bridge treats a browser as absent after about two minutes without presence. */
const BROWSER_ABSENCE_MS = 120_000;
/** Keep retrying through code-server's five-minute reconnection grace. */
const OWNERSHIP_RETRY_MS = 300_000;

function formatElapsed(ms: number) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Show the idle warning this long before automatic shutdown. */
const IDLE_WARNING_MS = 5 * 60_000;
/**
 * How long a launched editor may go without a confirmed extension connection before the
 * header says so. A cold code-server load through the gateway regularly takes half a minute.
 */
const CONNECTION_DELAY_MS = 90_000;

export function WorkshopIde({
  workspace,
  launch,
  visible,
  busy,
  backgroundSaving = false,
  enabled,
  editorConnected,
  editorTransportConnected = editorConnected,
  connectionError = null,
  hasAppPreview = false,
  colorScheme,
  operation = null,
  action = null,
  onRefresh,
  onReconnect,
  onCheckStatus,
  availability = 'openable',
  idleStopAt = null,
  onKeepWorking,
  keepingWorking = false,
  backedUpAt = null,
}: {
  workspace: CurrentWorkspace['workspace'];
  launch: ReturnType<typeof useIdeLaunch>;
  visible: boolean;
  busy: boolean;
  backgroundSaving?: boolean;
  enabled: boolean;
  editorConnected: boolean;
  editorTransportConnected?: boolean;
  connectionError?: Error | null;
  hasAppPreview?: boolean;
  colorScheme?: 'light' | 'dark';
  operation?: WorkspaceOperation | null;
  action?: WorkspaceAction | null;
  onRefresh: () => void;
  onReconnect: () => void;
  onCheckStatus?: () => void;
  /** Why compute cannot start for this activity, if it cannot. */
  availability?: WorkspaceAvailability;
  /** When automatic idle shutdown would pause the workspace without new activity. */
  idleStopAt?: string | null;
  onKeepWorking?: () => void;
  keepingWorking?: boolean;
  /** When the current activity's files were last verified in a checkpoint. */
  backedUpAt?: string | null;
}) {
  const context = workspace?.activeAttemptId
    ? `${workspace.id}:${workspace.generation}:${workspace.activeAttemptId}`
    : null;
  const [recoveryContext, setRecoveryContext] = useState<string | null>(null);
  const [opened, setOpened] = useState<{ context: string; grant: string } | null>(null);
  const [connectedGrant, setConnectedGrant] = useState<string | null>(null);
  const [connectionDelayed, setConnectionDelayed] = useState(false);
  const requested = useRef<string | null>(null);
  const ownershipRetry = useRef<{ context: string; startedAt: number } | null>(null);
  const submittedGrant = useRef<string | null>(null);
  const launchForm = useRef<HTMLFormElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  /** The grant whose editor document has finished loading in the frame. */
  const [loadedGrant, setLoadedGrant] = useState<string | null>(null);
  // This browsing context exists before the workspace query resolves. Keep its
  // target name stable so a later form submission reuses the existing iframe.
  const frameName = `mio-workshop-${useId()}`;
  const [retryStartedAt, setRetryStartedAt] = useState<number | null>(null);
  const [retryNow, setRetryNow] = useState(() => Date.now());
  useEffect(() => {
    if (retryStartedAt === null) return;
    setRetryNow(Date.now());
    const timer = setInterval(() => setRetryNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [retryStartedAt]);
  const ready = workspace?.state === 'ready';
  const previousSessionElapsedMs = retryStartedAt === null ? null : retryNow - retryStartedAt;
  const previousSessionCleared =
    previousSessionElapsedMs !== null && previousSessionElapsedMs >= BROWSER_ABSENCE_MS;
  const launchError = ready ? launch.error : null;
  const previousSession =
    launchError instanceof ApiRequestError &&
    ['editor_in_use', 'editor_session_changed'].includes(launchError.code);
  // A launch can fail because the workspace's file service or the platform did
  // not answer in time (a Coder app proxy 502, a lost response). Those clear on
  // their own, so they get the same bounded retry as a clearing session.
  const transientLaunch =
    launchError instanceof ApiRequestError &&
    !previousSession &&
    (launchError.status >= 500 ||
      launchError.status === 0 ||
      ['workspace_bridge_unavailable', 'workspace_unavailable', 'workspace_not_ready'].includes(
        launchError.code,
      ));
  const refresh = () => {
    if (previousSession) setRecoveryContext(context);
    else onRefresh();
  };
  const { mutate, isPending } = launch;
  useEffect(() => {
    if (
      !visible ||
      !enabled ||
      busy ||
      !ready ||
      !context ||
      isPending ||
      requested.current === context
    )
      return;
    requested.current = context;
    mutate();
  }, [visible, enabled, busy, ready, context, isPending, mutate]);
  useEffect(() => {
    const data = launch.data;
    if (
      data?.context !== context ||
      !context ||
      busy ||
      !ready ||
      !launchForm.current ||
      submittedGrant.current === data.launch.grant
    )
      return;
    submittedGrant.current = data.launch.grant;
    launchForm.current.submit();
    setOpened({ context, grant: data.launch.grant });
  }, [launch.data, context, busy, ready]);
  const ideVisible = ready && opened?.context === context && context !== null;
  // Compute that paused, failed or moved to another activity leaves a stale editor window
  // behind: unload it so it stops reconnecting, and let the next ready context launch anew.
  useEffect(() => {
    if (!opened || (ready && opened.context === context)) return;
    try {
      frame.current?.contentWindow?.location.replace('about:blank');
    } catch {
      // A document that refuses navigation is replaced by the next grant anyway.
    }
    setOpened(null);
    setConnectedGrant(null);
    setLoadedGrant(null);
    submittedGrant.current = null;
    requested.current = null;
  }, [opened, ready, context]);
  useEffect(() => {
    if (!context || ideVisible) {
      ownershipRetry.current = null;
      setRetryStartedAt(null);
      return;
    }
    if (!visible || !enabled || busy || isPending || recoveryContext === context) return;
    if (!previousSession && !transientLaunch) {
      ownershipRetry.current = null;
      setRetryStartedAt(null);
      return;
    }
    if (ownershipRetry.current?.context !== context) {
      ownershipRetry.current = { context, startedAt: Date.now() };
      setRetryStartedAt(ownershipRetry.current.startedAt);
    }
    // Retry ordinary admission while the bridge proves the previous browser
    // absent (about two minutes) and through code-server's reconnection grace
    // (five minutes). An established iframe is never replaced here.
    const remaining = OWNERSHIP_RETRY_MS - (Date.now() - ownershipRetry.current.startedAt);
    if (remaining <= 0) return;
    const timer = setTimeout(() => mutate(), Math.min(5000, remaining));
    return () => clearTimeout(timer);
  }, [
    context,
    ideVisible,
    visible,
    enabled,
    busy,
    isPending,
    previousSession,
    transientLaunch,
    recoveryContext,
    mutate,
  ]);
  useEffect(() => {
    if (editorTransportConnected && ideVisible && opened) setConnectedGrant(opened.grant);
  }, [editorTransportConnected, ideVisible, opened]);
  useEffect(() => {
    setConnectionDelayed(false);
    if (!ready || !context || busy || editorConnected || isPending || launch.error) return;
    const timer = setTimeout(() => setConnectionDelayed(true), CONNECTION_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, context, busy, editorConnected, isPending, launch.error, opened]);
  // The editor is shown as soon as its document has loaded: a learner should watch the
  // IDE come up, not a spinner. A status probe is separate from VS Code's connection, so
  // an established editor keeps its own reconnect UI while probes recover; the page still
  // requires a confirmed connection before save/switch/submit commands.
  const editorAvailable =
    ideVisible &&
    (loadedGrant === opened?.grant || editorTransportConnected || connectedGrant === opened?.grant);
  const editorBlocked = busy && !backgroundSaving;
  // Warn before automatic idle shutdown so reading the brief or thinking does
  // not end in a silent pause; "Keep working" counts as activity.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!idleStopAt && !backedUpAt) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [idleStopAt, backedUpAt]);
  const backup = ready && !busy && enabled ? backupAge(backedUpAt, now) : null;
  const idleRemainingMs = idleStopAt ? Date.parse(idleStopAt) - now : null;
  const idleWarning =
    ready &&
    enabled &&
    !busy &&
    idleRemainingMs !== null &&
    Number.isFinite(idleRemainingMs) &&
    // A deadline already in the past is a stale activity timestamp from before the
    // resume, not an imminent pause; the server reports a real pause as 'stopping'.
    idleRemainingMs > 0 &&
    idleRemainingMs <= IDLE_WARNING_MS;
  const connectionFailed =
    ready && !busy && !editorTransportConnected && (connectionDelayed || Boolean(connectionError));
  const progress = workspaceProgress({
    workspace,
    operation: busy || operation?.status === 'failed' ? operation : null,
    action: busy ? action : null,
    busy,
    connected: editorConnected,
    enabled,
    launchFailed: Boolean(launchError) || connectionFailed,
    availability,
  });
  const openable = availability === 'openable';
  const paused = workspace?.state === 'stopped' && !busy && enabled && openable;
  const fullscreen = useEditorFullscreen(visible);
  const fieldActive = Boolean(workspace?.fieldActive) && !busy;
  return (
    <aside
      id="workshop-ide"
      tabIndex={-1}
      className={cn(
        'workshop-editor bg-canvas flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
        fullscreen.className,
      )}
      aria-label="Coding environment"
    >
      {fullscreen.fullscreen ? (
        <UsageBalance
          compact
          onManage={fullscreen.exit}
          className="border-line shrink-0 border-b px-4 py-2"
        />
      ) : null}
      <div className="border-line flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b px-3 py-2">
        <div className="mr-auto flex min-w-0 items-center gap-3">
          <h2 className="text-fg inline-flex items-center gap-2 text-xs font-semibold">
            <Code2 className="size-4" aria-hidden="true" />
            Editor
          </h2>
          <p className="text-fg-muted inline-flex items-center gap-1.5 text-xs" role="status">
            <span
              className={cn(
                'size-1.5 shrink-0 rounded-full',
                enabled && ready && editorConnected && !editorBlocked
                  ? 'bg-success'
                  : 'bg-fg-subtle',
              )}
              aria-hidden="true"
            />
            {workspace?.state === 'stopping' && progress.failed
              ? progress.title
              : !enabled
                ? 'Coding is unavailable'
                : fieldActive
                  ? 'Workbench active'
                  : workspace?.state === 'failed' ||
                      (launchError && !previousSession && !transientLaunch)
                    ? 'Connection needs attention'
                    : previousSession
                      ? 'Previous session still linked'
                      : transientLaunch
                        ? 'Retrying connection…'
                        : connectionFailed
                          ? 'Still connecting…'
                          : backgroundSaving && editorConnected
                            ? 'Connected · backing up…'
                            : busy
                              ? progress.title + '…'
                              : ready
                                ? editorConnected
                                  ? 'Connected'
                                  : editorTransportConnected
                                    ? 'Opening workshop files…'
                                    : connectedGrant === opened?.grant
                                      ? 'Reconnecting…'
                                      : 'Connecting…'
                                : workspace?.state === 'stopped'
                                  ? 'Paused'
                                  : !openable && (!workspace || workspace.state === 'allocated')
                                    ? progress.title
                                    : progress.running
                                      ? 'Preparing your IDE…'
                                      : progress.title}
          </p>
          {backup ? (
            <p className="text-fg-subtle hidden text-xs md:block" title={backedUpAt ?? undefined}>
              {backup}
            </p>
          ) : null}
        </div>
        {idleWarning ? (
          <div
            role="status"
            className="border-line bg-surface text-fg order-last flex basis-full flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-xs"
          >
            <span className="min-w-0 flex-1">
              {idleRemainingMs !== null && idleRemainingMs > 60_000
                ? `Your workspace pauses in about ${Math.ceil(idleRemainingMs / 60_000)} minutes unless you keep working. Your files are saved first.`
                : 'Your workspace is about to pause because it has been idle. Your files are saved first.'}
            </span>
            {onKeepWorking ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={onKeepWorking}
                loading={keepingWorking}
                disabled={keepingWorking}
              >
                Keep working
              </Button>
            ) : null}
          </div>
        ) : null}
        {hasAppPreview && ready && workspace ? (
          <WorkshopPreview workspace={workspace} disabled={busy || !enabled} />
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          className="px-2"
          onClick={fullscreen.toggle}
          aria-pressed={fullscreen.fullscreen}
          aria-label={fullscreen.fullscreen ? 'Exit full screen' : 'Full screen editor'}
          title={
            fullscreen.fullscreen
              ? 'Exit full screen (Esc)'
              : 'Expand the editor to the whole window'
          }
        >
          {fullscreen.fullscreen ? (
            <Minimize2 className="size-3.5" aria-hidden="true" />
          ) : (
            <Maximize2 className="size-3.5" aria-hidden="true" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="px-2"
          onClick={refresh}
          disabled={
            busy ||
            !enabled ||
            !openable ||
            fieldActive ||
            !workspace ||
            !['ready', 'stopped', 'failed', 'allocated'].includes(workspace.state)
          }
          loading={isPending}
          title={
            !openable
              ? progress.description
              : workspace?.state === 'stopped'
                ? 'Resume the paused environment with your saved files.'
                : 'Save your connected editor before refreshing. Refresh reconnects; it never resets your files.'
          }
          aria-label="Refresh IDE"
        >
          <RefreshCw className="size-3.5" aria-hidden="true" />
          <span className="workshop-refresh-label">Refresh IDE</span>
        </Button>
        <details
          className="group relative text-xs"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.currentTarget.open = false;
              event.currentTarget.querySelector('summary')?.focus();
            }
          }}
        >
          <summary
            aria-label="IDE help"
            title="IDE help"
            className="text-fg-muted hover:text-fg hover:bg-surface focus-visible:ring-ring flex size-8 cursor-pointer list-none items-center justify-center rounded-md outline-none focus-visible:ring-2 [&::-webkit-details-marker]:hidden"
          >
            <HelpCircle className="size-4" aria-hidden="true" />
          </summary>
          <div className="border-line bg-canvas absolute right-0 top-full z-20 mt-2 max-h-[60dvh] w-72 overflow-y-auto rounded-lg border p-5 shadow-lg">
            <h3 className="text-fg mb-3 text-sm font-semibold">Working in your IDE</h3>
            <ol className="list-decimal space-y-2 pl-4 leading-5">
              <li>
                Open <code>README.md</code> in Explorer for the task and file map.
              </li>
              <li>
                Use Terminal → New Terminal, then run the README commands from the activity folder.
              </li>
              <li>
                Named text files auto-save after a short delay by default. Source backups run about
                every five minutes and before changing workshops, submitting or pausing.
              </li>
              <li>
                The editor follows the platform’s light or dark appearance unless you choose your
                own theme in IDE settings.
              </li>
              <li>
                Moving between Workshop, Studio and Workbench keeps your IDE running and autosave
                active; there is nothing to save or stop by hand.
              </li>
              <li>
                After 30 minutes without editor interaction, the environment saves and pauses on its
                own. You get a warning five minutes before, and Resume workspace brings it back with
                your files.
              </li>
            </ol>
            {hasAppPreview ? (
              <p className="border-line mt-3 border-t pt-3 leading-5">
                Start your server on port 8000 using the command in this workshop’s README, then
                choose <strong>Open app</strong>. This opens the workspace application in a separate
                preview tab.
              </p>
            ) : null}
            <p className="mt-3 leading-5">
              Refresh reconnects the editor; it does not reset your files or restart your
              application. Backups exclude database rows, dependencies and secrets. Name new files
              so they can be saved.
            </p>
          </div>
        </details>
      </div>
      {launchError ? (
        <div className="shrink-0 px-4 pt-4">
          <Alert tone={previousSession || transientLaunch ? 'info' : 'danger'}>
            {previousSession
              ? 'A previous editor session is still linked to this workspace.'
              : transientLaunch
                ? 'The editor service did not answer. Retrying for a couple of minutes; use Refresh IDE if it does not recover.'
                : launchError.message}
          </Alert>
        </div>
      ) : null}
      {ready &&
      !busy &&
      !editorConnected &&
      !launch.error &&
      (editorAvailable || connectionFailed) ? (
        <div className="border-line shrink-0 border-b px-4 py-3 text-sm leading-6" role="status">
          {editorTransportConnected
            ? 'Your workshop files are still opening. If the editor stays empty or shows another folder, save any open files in the editor below before reloading this page.'
            : connectionFailed
              ? 'The editor connection is taking longer than usual. Your IDE is shown as it loads; saving, switching and submitting unlock once the connection is confirmed. Use Refresh IDE if it does not recover.'
              : 'Reconnecting to your editor. Keep this tab open; saving and submitting will be available once the connection returns.'}
        </div>
      ) : null}
      {previousSession ? (
        <div className="border-line bg-sidebar mx-4 mt-3 space-y-3 rounded-lg border p-4 text-sm leading-6">
          <p role="status">
            {previousSessionCleared
              ? `The previous connection has had ${formatElapsed(previousSessionElapsedMs)} to clear. If that tab is closed, reopen your saved files here.`
              : `If the previous tab is closed, we keep retrying while its connection clears, which takes about two minutes${previousSessionElapsedMs !== null ? ` (${formatElapsed(previousSessionElapsedMs)} so far)` : ''}. If it is still open, save and close it first.`}
          </p>
          <Button
            variant={previousSessionCleared ? 'primary' : 'secondary'}
            disabled={busy || !enabled || isPending}
            onClick={() => setRecoveryContext(context)}
          >
            Reopen IDE here
          </Button>
          <p className="text-fg-muted text-xs">
            Reopening restores your saved files; unsaved changes left in the previous editor cannot
            be recovered.
          </p>
        </div>
      ) : null}
      <ConfirmationDialog
        open={visible && ready && context !== null && recoveryContext === context}
        title="Reopen the IDE here?"
        confirmLabel="Reopen saved files"
        cancelLabel="Cancel"
        busy={busy || isPending}
        onCancel={() => setRecoveryContext(null)}
        onConfirm={() => {
          if (!enabled || busy || isPending || recoveryContext !== context) return;
          setRecoveryContext(null);
          onReconnect();
          mutate({ recoverDisconnected: true });
        }}
      >
        Saved workspace files will remain. Unsaved changes in the previous editor cannot be
        recovered. An editor that is still connected must be saved and closed first.
      </ConfirmationDialog>
      {launch.data?.context === context ? (
        <form ref={launchForm} method="POST" action={launch.data.launch.action} target={frameName}>
          <input type="hidden" name="grant" value={launch.data.launch.grant} />
        </form>
      ) : null}
      <div
        className="bg-canvas relative min-h-0 flex-1 overflow-hidden"
        aria-busy={progress.running && (editorBlocked || !editorAvailable)}
      >
        {/* Keep the frame mounted through polling, switching, and trips back to Studio. */}
        <iframe
          ref={frame}
          title="Workshop IDE"
          onLoad={() => {
            if (submittedGrant.current) setLoadedGrant(submittedGrant.current);
          }}
          style={colorScheme ? { colorScheme } : undefined}
          name={frameName}
          sandbox="allow-scripts allow-same-origin allow-forms allow-downloads allow-modals allow-popups"
          referrerPolicy="no-referrer"
          allow="clipboard-read; clipboard-write"
          inert={editorBlocked || !editorAvailable}
          className={`size-full ${ideVisible ? '' : 'hidden'}`}
        />
        {editorBlocked || !editorAvailable ? (
          <div className="bg-canvas/95 absolute inset-0 flex items-center justify-center overflow-y-auto p-8">
            <div>
              <WorkshopProgress
                model={progress}
                operationKey={`${context}:${operation?.id ?? 'connect'}`}
                {...(onCheckStatus ? { onCheckStatus } : {})}
              />
              {ready && !busy && (connectionFailed || launch.error) ? (
                <Button className="mt-4" onClick={refresh} disabled={!enabled || isPending}>
                  {previousSession ? 'Reconnect to saved files' : 'Retry connection'}
                </Button>
              ) : null}
              {paused ? (
                <Button className="mt-4" onClick={onRefresh} disabled={isPending}>
                  Resume workspace
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </aside>
  );
}
